<template>
    <div class="analyst-stock-item" :class="upDownColorClass">
        <!-- 上部：股票信息 -->
        <a class="analyst-stock-item__header">
            <div class="analyst-stock-item__left">
                <StockLogo :stock-id="stockId" :stock-name="name" />
                <div class="analyst-stock-item__info">
                    <div class="analyst-stock-item__name-row">
                        <span class="analyst-stock-item__market">{{ market }}</span>
                        <span class="analyst-stock-item__name">{{ name }}</span>
                    </div>
                    <PriceChange :price="price" :change="change" :up-down-color="upDownColor" />
                </div>
            </div>
            <div class="analyst-stock-item__right">
                <div class="analyst-stock-item__stat">
                    <span class="analyst-stock-item__stat-value">{{ successRate }}%</span>
                    <span class="analyst-stock-item__stat-label">{{ $t('success_rate') }}</span>
                </div>
                <div class="analyst-stock-item__stat">
                    <span class="analyst-stock-item__stat-value" :class="returnClass">{{ formattedReturn }}</span>
                    <span class="analyst-stock-item__stat-label">{{ $t('average_return') }}</span>
                </div>
                <FavoriteButton :count="favoriteCount" :active="isFavorite" :show-count="showCount" @click.stop.prevent="handleFavoriteClick" />
            </div>
        </a>

        <!-- 下部：评级信息 -->
        <a class="analyst-stock-item__footer">
            <div class="analyst-stock-item__rating-info-row">
                <div class="analyst-stock-item__rating-info">
                    <span class="analyst-stock-item__label">{{ $t('rating') }}</span>
                    <span class="analyst-stock-item__rating" :class="ratingClass">{{ rating }}</span>
                </div>

                <div class="analyst-stock-item__rating-info">
                    <span class="analyst-stock-item__label">{{ $t('target') }}</span>
                    <span class="analyst-stock-item__target">{{ target }}</span>
                </div>
            </div>

            <div class="analyst-stock-item__date-info">
                <LatestBadge v-if="isLatest" :text="$t('latest')" />
                <span class="analyst-stock-item__date">{{ date }}</span>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                        d="M6 4L10 8L6 12"
                        stroke="#999"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    />
                </svg>
            </div>
        </a>
    </div>
</template>

<script >
import { computed } from 'vue-demi';
import FavoriteButton from '../../assets/uis/FavoriteButton.vue';
import LatestBadge from '../../assets/uis/LatestBadge.vue';
import PriceChange from '../../assets/uis/PriceChange.vue';
import StockLogo from '../../assets/uis/StockLogo.vue';
import { UP_DOWN_COLOR } from '../../common/enum';
import { LanguageId, useI18n } from '../../common/useI18n';
const __sfc_main = {};
__sfc_main.props = {
  logo: {
    type: String,
    default: ''
  },
  market: {
    type: String,
    default: 'US'
  },
  name: {
    type: String,
    default: ''
  },
  price: {
    type: Number,
    default: 0
  },
  change: {
    type: Number,
    default: 0
  },
  successRate: {
    type: Number,
    default: 0
  },
  averageReturn: {
    type: Number,
    default: 0
  },
  favoriteCount: {
    type: Number,
    default: 0
  },
  isFavorite: {
    type: Boolean,
    default: false
  },
  showCount: {
    type: Boolean,
    default: true
  },
  rating: {
    type: String,
    default: 'Buy'
  },
  target: {
    type: [String, Number],
    default: ''
  },
  date: {
    type: String,
    default: ''
  },
  isLatest: {
    type: Boolean,
    default: false
  },
  languageId: {
    type: Number,
    default: LanguageId.EN
  },
  upDownColor: {
    type: Number,
    default: UP_DOWN_COLOR.GREEN_UP
  },
  stockId: {
    type: String,
    default: ''
  }
};
__sfc_main.setup = (__props, __ctx) => {
  const props = __props;
  const emit = __ctx.emit;

  // 多语言
  const $t = useI18n(props.languageId);

  // 点击自选按钮
  const handleFavoriteClick = $event => {
    emit('toggle-favorite', $event);
  };
  const formattedReturn = computed(() => {
    const sign = props.averageReturn >= 0 ? '+' : '';
    return `${sign}${props.averageReturn.toFixed(2)}%`;
  });

  // 涨跌色类名
  const upDownColorClass = computed(() => {
    return props.upDownColor === UP_DOWN_COLOR.RED_UP ? 'analyst-stock-item--red-up' : 'analyst-stock-item--green-up';
  });
  const returnClass = computed(() => {
    if (props.averageReturn > 0) return 'analyst-stock-item__stat-value--positive';
    if (props.averageReturn < 0) return 'analyst-stock-item__stat-value--negative';
    return '';
  });
  const ratingClass = computed(() => {
    const r = props.rating.toLowerCase();
    if (r === 'buy' || r === 'strong buy' || r === '买入') return 'analyst-stock-item__rating--buy';
    if (r === 'sell' || r === 'strong sell' || r === '卖出') return 'analyst-stock-item__rating--sell';
    return 'analyst-stock-item__rating--hold';
  });
  return {
    $t,
    handleFavoriteClick,
    formattedReturn,
    upDownColorClass,
    returnClass,
    ratingClass
  };
};
__sfc_main.components = Object.assign({
  StockLogo,
  PriceChange,
  FavoriteButton,
  LatestBadge
}, __sfc_main.components);
export default __sfc_main;
</script>

<style lang="less" scoped>
.analyst-stock-item {
    padding: 0.24rem 0;

    &__header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.2rem;
    }

    &__left {
        display: flex;
        align-items: center;
        gap: 0.1rem;
    }

    &__logo {
        width: 0.65rem;
        height: 0.65rem;
        border-radius: 0.325rem;
        overflow: hidden;
        background: #f5f5f5;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
        }
    }

    &__logo-placeholder {
        font-size: 0.24rem;
        font-weight: 600;
        color: #999;
    }

    &__info {
        display: flex;
        flex-direction: column;
        gap: 0.04rem;
    }

    &__name-row {
        display: flex;
        align-items: center;
        gap: 0.08rem;
    }

    &__market {
        font-size: 0.22rem;
        font-weight: 400;

        height: 0.26rem;
        padding: 0 0.048rem;
        border-radius: 0.048rem;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.2rem;
        font-weight: 400;
        color: #fff;
        line-height: 100%;
        background-color: #00a4ff;
    }

    &__name {
        font-size: 0.28rem;
        font-weight: 600;
        color: #000;
    }

    &__right {
        display: flex;
        align-items: center;
        gap: 0.24rem;
    }

    &__stat {
        display: flex;
        flex-direction: column;
        gap: 0.04rem;
    }

    &__stat-value {
        font-size: 0.24rem;
        font-weight: 600;
        color: #333;
    }

    &__stat-label {
        font-size: 0.18rem;
        font-weight: 400;
        color: #999;
    }

    &__footer {
        display: flex;
        justify-content: space-between;
        align-items: center;

        height: 0.54rem;
        border-radius: 0.05rem;
        padding-left: 0.2rem;
        background-color: #f5f5f5;
    }

    &__rating-info-row {
        display: flex;
        flex-direction: row;
        gap: 0.3rem;
    }

    &__rating-info {
        display: flex;
        align-items: center;
        gap: 0.08rem;
    }

    &__label {
        font-size: 0.22rem;
        font-weight: 400;
        color: #626262;
    }

    &__rating {
        font-size: 0.26rem;

        &--buy {
            color: #00a843;
        }

        &--sell {
            color: #d12626;
        }

        &--hold {
            color: #626262;
        }
    }

    &__target {
        font-size: 0.24rem;
        font-weight: 500;
        color: #333;
    }

    &__date-info {
        display: flex;
        align-items: center;
        gap: 0.1rem;

        svg {
            width: 0.28rem;
            height: 0.28rem;
        }
    }

    &__date {
        font-size: 0.22rem;
        color: #8d8d8d;
    }

    // 绿涨红跌（默认）
    &--green-up {
        .analyst-stock-item__stat-value--positive {
            color: #52c41a;
        }
        .analyst-stock-item__stat-value--negative {
            color: #ff4d4f;
        }
    }

    // 红涨绿跌
    &--red-up {
        .analyst-stock-item__stat-value--positive {
            color: #ff4d4f;
        }
        .analyst-stock-item__stat-value--negative {
            color: #52c41a;
        }
    }
}
</style>
